import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { PlaceContract } from '@polaris-customerweb/sharedcomponents.core';
import { Place, PremiumPlace } from '@polaris-customerweb/sharedcomponents.map';
import {
    GeoBounds,
    GeoService,
    MailingAddress,
} from '@polaris-customerweb/sharedcomponents.shared';
import { find, get } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SetPlacesInView } from 'src/app/components/point-of-interest/point-of-interest.actions';
import { selectAllPlacesInView } from 'src/app/components/point-of-interest/point-of-interest.reducer';
import { AppState } from 'src/app/models/app-state';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { PlaceResourceService } from 'src/app/services/place-resource.service';
import { MapboxGeocodeFeature } from './geocoder-resource.service';

@Injectable({ providedIn: 'root' })
export class PlaceService {
    private cachedPlaces: (Place | PremiumPlace)[];
    private placeTypeI18n = {
        bank: $localize`Bank`,
        bar: $localize`Bar`,
        business: $localize`Business`,
        campground: $localize`Campground`,
        food: $localize`Food`,
        fuel: $localize`Fuel`,
        lodging: $localize`Lodging`,
        medical: $localize`Medical`,
        other: $localize`Other`,
        park: $localize`Park`,
        parking: $localize`Parking`,
        shopping: $localize`Shopping`,
        unknown: $localize`Point of Interest`,
    };

    constructor(
        private configService: ConfigService,
        private placeResource: PlaceResourceService,
        private store: Store<AppState>
    ) {
        this.store
            .pipe(select(selectAllPlacesInView))
            .subscribe((placesInView) => (this.cachedPlaces = placesInView));
    }

    //#region Get
    getById(
        id: string,
        cachedResultOnly = false
    ): Observable<Place | PremiumPlace | undefined> {
        const cachedResult = this.getByIdFromCache(id);
        if (cachedResult) {
            return of(cachedResult);
        }

        if (cachedResultOnly) {
            return of(undefined);
        }

        return this.placeResource
            .getById(id)
            .pipe(
                map((placeContract) => this.placeContractToPlace(placeContract))
            );
    }

    getByCenterAndRadius(
        lat: number,
        lng: number,
        radius: number,
        includeStandard = true,
        excludedTypes: string[] = [],
        includeSponsors = false,
        maxPoiResults = this.configService.maxPoiResults,
        zoom?: number
    ): Observable<(Place | PremiumPlace)[]> {
        return this.placeResource
            .getByBounds(
                lat,
                lng,
                radius,
                maxPoiResults,
                zoom,
                includeSponsors,
                includeStandard,
                excludedTypes
            )
            .pipe(
                map((placeCollectionDto) => {
                    return (placeCollectionDto.features ?? []).map(
                        (placeContract) => {
                            if (placeContract.properties?.isSponsor) {
                                return new PremiumPlace(placeContract);
                            } else {
                                return new Place(placeContract);
                            }
                        }
                    );
                }),
                tap((places) => {
                    this.store.dispatch(new SetPlacesInView(places));
                })
            );
    }

    getByBounds(
        bounds: GeoBounds,
        includeStandard = true,
        excludedTypes: string[] = [],
        includeSponsors = false,
        maxPoiResults = this.configService.maxPoiResults,
        zoom?: number
    ): Observable<(Place | PremiumPlace)[]> {
        const boundsDiameterRadians = GeoService.pointsToRadians(
            bounds.north,
            bounds.east,
            bounds.south,
            bounds.west
        );
        const boundsDiameter = GeoService.radiansToMeters(
            boundsDiameterRadians
        );
        const boundsRadius = Math.round(boundsDiameter * 500) / 1000;
        const lat = GeoService.getBoundLat(bounds.centerGeoCoordinate.lat);
        const lng = GeoService.getBoundLng(bounds.centerGeoCoordinate.lng);

        return this.getByCenterAndRadius(
            lat,
            lng,
            boundsRadius,
            includeStandard,
            excludedTypes,
            includeSponsors,
            maxPoiResults,
            zoom
        );
    }

    getByIdFromCache(id: string): Place | PremiumPlace | undefined {
        return find(this.cachedPlaces, { factualId: id });
    }

    getAddressFromFeature(feature: MapboxGeocodeFeature): MailingAddress {
        // Try to get the address components from the feature
        const number = get(feature, 'address');
        let street = '';
        let state = '';
        let city = '';
        let postcode = '';
        let country = '';

        // Figure out what type of feature this is
        const type = feature.place_type[0];
        if (type === 'address') {
            street = get(feature, 'text');
        } else if (type === 'place') {
            city = get(feature, 'text');
        }

        // Loop through the context to find the rest
        for (const context of feature.context) {
            if (context.id.startsWith('region')) {
                state = context.short_code?.split('-').pop() || '';
            } else if (context.id.startsWith('country')) {
                country = context.short_code || '';
            } else if (context.id.startsWith('postcode')) {
                postcode = context.text || '';
            } else if (context.id.startsWith('place')) {
                city = context.text || '';
            }
        }

        const fullStreet = number && street ? `${number} ${street}` : '';

        // Create the address object
        const address = new MailingAddress({
            city,
            country,
            postalCode: postcode,
            state,
            street: fullStreet,
        });

        return address;
    }

    //#endregion

    placesToGeoJson(places: Place[] = []): GeoJsonFeatureCollection {
        const features = places.map((place) => place.toGeoJson());
        return new GeoJsonFeatureCollection(features);
    }

    getPlaceTypeI18n(place: string) {
        return (
            this.placeTypeI18n[place?.toLowerCase()] ||
            this.placeTypeI18n.unknown
        );
    }

    private placeContractToPlace(dto: PlaceContract): Place | PremiumPlace {
        if (dto.properties?.isSponsor) {
            return new PremiumPlace(dto);
        } else {
            return new Place(dto);
        }
    }
}
